import {
  Component,
  ElementRef,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import * as THREE from 'three';
import { GLTFLoader } from 'three-stdlib';
import { OrbitControls, TransformControls } from 'three-stdlib';
 
import { WoundAnalysisMetrics } from '../../../../core/models/wound/wound.model';

@Component({
  selector: 'app-model-viewer-3d',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './model-viewer-3d.component.html',
  styleUrls: ['./model-viewer-3d.component.scss'],
})
export class ModelViewer3DComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('canvasContainer') canvasRef!: ElementRef<HTMLDivElement>;

  @Input() modelUrl: string | null = null;
  @Input() markers: THREE.Mesh[] = [];
  @Input() isAddMarkerMode: boolean = false;
  @Input() isGizmoMode: boolean = false;
  @Input() markersVisible: boolean = true;
  @Input() isMeasureDistanceMode: boolean = false;
  @Input() isMeasureAngleMode: boolean = false;
  @Input() isMeasureAreaMode: boolean = false;
  @Input() woundMetrics: WoundAnalysisMetrics | null = null;

  @Output() markerClick = new EventEmitter<number>();
  @Output() markerAdd = new EventEmitter<THREE.Vector3>();
  @Output() modelLoaded = new EventEmitter<void>();
  @Output() modelLoadError = new EventEmitter<Error>();
  @Output() measurementResult = new EventEmitter<{
    type: string;
    value: number;
    unit: string;
    label: string;
    extra?: { perimeter?: number; perimeterLabel?: string; areaLabel?: string };
  }>();

  private translate = inject(TranslateService);

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private controls!: OrbitControls;
  private transformControls!: TransformControls;
  private model: THREE.Group | null = null;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private animationId: number | null = null;
  private markerScale: number = 0.1;
  private refCameraDistance: number = 1;

  private initialCameraPosition = new THREE.Vector3();
  private initialCameraTarget = new THREE.Vector3();

  private ambientLight!: THREE.AmbientLight;
  private dirLight!: THREE.DirectionalLight;
  private hemiLight!: THREE.HemisphereLight;

  private brightness: number = 1.0;
  private isHighContrast: boolean = false;

  private originalMaterials: Map<string, THREE.Material | THREE.Material[]> =
    new Map();
  private isTextureEnabled: boolean = true;

  private resizeObserver: ResizeObserver | null = null;

  // Measurement state
  private measurePoints: THREE.Vector3[] = [];
  private measureDots: THREE.Mesh[] = [];
  private measureLines: THREE.Line[] = [];
  private measureLabels: THREE.Sprite[] = [];
  // Scale factor: Three.js world units -> mm (metric-space sabit)
  private measureScaleFactor: number = 200;
  // Area tool: live preview line (last point → cursor)
  private previewLine: THREE.Line | null = null;
  // Area tool: in-progress perimeter sum (Three.js units)
  private areaPerimeterRaw: number = 0;
  // Outputs for in-progress point count (sidebar uses it)
  areaPointCount: number = 0;
  private readonly fallbackMeasurementColorHex = '#009448';

  ngAfterViewInit(): void {
    this.initScene();
    if (this.modelUrl) {
      this.loadModel(this.modelUrl);
    }

    if (this.canvasRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      this.resizeObserver.observe(this.canvasRef.nativeElement);
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelUrl'] && !changes['modelUrl'].firstChange) {
      const newUrl = changes['modelUrl'].currentValue;
      if (newUrl) {
        this.clearModel();
        this.loadModel(newUrl);
      }
    }

    if (changes['isGizmoMode']) {
      this.handleGizmoModeChange();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.destroyScene();
  }

  private initScene(): void {
    if (!this.canvasRef) return;

    const container = this.canvasRef.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000000);
    this.scene.fog = new THREE.Fog(0x000000, 40, 120);

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(0, 5, 45);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    container.appendChild(this.renderer.domElement);

    this.renderer.domElement.addEventListener(
      'wheel',
      this.handleWheel.bind(this),
      { passive: false },
    );

    this.renderer.domElement.addEventListener(
      'touchstart',
      this.handleTouchStart.bind(this),
      { passive: false },
    );

    this.renderer.domElement.addEventListener(
      'touchend',
      this.handleTouchEnd.bind(this),
      { passive: false },
    );

    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(this.ambientLight);

    this.hemiLight = new THREE.HemisphereLight(0xddeeff, 0x202020, 0.6);
    this.scene.add(this.hemiLight);

    this.dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    this.dirLight.position.set(10, 20, 10);
    this.dirLight.castShadow = true;
    this.scene.add(this.dirLight);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(200, 200),
      new THREE.ShadowMaterial({ opacity: 0.3 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.minDistance = 4;
    this.controls.maxDistance = 60;
    this.controls.target.set(0, 3, 0);
    this.controls.update();

    this.transformControls = new TransformControls(
      this.camera,
      this.renderer.domElement,
    );
    this.transformControls.addEventListener(
      'dragging-changed' as any,
      (event: any) => {
        this.controls.enabled = !event.value;
      },
    );
    this.scene.add(this.transformControls);

    if (!this.animationId) this.animate();
  }

  private destroyScene(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    this.clearModel();

    if (this.transformControls) {
      this.transformControls.dispose();
    }

    if (this.controls) {
      this.controls.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
      if (
        this.canvasRef &&
        this.canvasRef.nativeElement.contains(this.renderer.domElement)
      ) {
        this.renderer.domElement.removeEventListener(
          'wheel',
          this.handleWheel.bind(this),
        );
        this.renderer.domElement.removeEventListener(
          'touchstart',
          this.handleTouchStart.bind(this),
        );
        this.renderer.domElement.removeEventListener(
          'touchend',
          this.handleTouchEnd.bind(this),
        );
        this.canvasRef.nativeElement.removeChild(this.renderer.domElement);
      }
    }

    console.log('🗑️ 3D Scene destroyed');
  }

  private loadModel(url: string): void {
    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => {
        this.clearModel();
        this.model = gltf.scene;
        this.scene.add(this.model); // Modeli sahneye ekle

        // --- Dinamik Kamera Konumlandırma Başlangıcı ---

        // 1. Modelin sınırlayıcı kutusunu (bounding box) hesapla
        const box = new THREE.Box3().setFromObject(this.model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        this.model.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        this.markerScale = maxDim * 0.005;

        const fov = this.camera.fov * (Math.PI / 180);

        let cameraDistance = maxDim / (2 * Math.tan(fov / 2));

        cameraDistance *= 1.7;

        this.camera.position.set(0, maxDim * 0.3, cameraDistance);

        this.controls.target.set(0, 0, 0);

        this.controls.minDistance = cameraDistance / 5;
        this.controls.maxDistance = cameraDistance * 5;

        this.controls.update();
        this.refCameraDistance = this.camera.position.distanceTo(this.controls.target);

        this.originalMaterials.clear();
        this.model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            this.originalMaterials.set(child.uuid, child.material);
            if (child.material) {
              const materials = Array.isArray(child.material)
                ? child.material
                : [child.material];

              materials.forEach((mat) => {
                if (mat instanceof THREE.MeshStandardMaterial) {
                  mat.roughness = 1.0;
                  mat.metalness = 0.0;
                  mat.needsUpdate = true;
                }
              });
            }
          }
        });

        if (!this.isTextureEnabled) {
          this.setTextureEnabled(false);
        }
        this.initialCameraPosition.copy(this.camera.position);
        this.initialCameraTarget.copy(this.controls.target);

        // Metric-space modeller için sabit dönüşüm:
        // 1 world unit = 1 metre kabul edilir.
        // Ölçüm çıktılarında mm kullanıldığı için m -> mm dönüşümü 1000 olurdu.
        // Ancak backend ve mevcut dataset ile kalibrasyon için 1/10 ölçek uygulanır.
        // Net: 1 world unit -> 100 mm (10 cm).
        this.measureScaleFactor = 200;

        this.modelLoaded.emit();
        console.log('3D Model loaded:', url);
      },
      undefined,
      (error) => {
        console.error('Error loading 3D model:', error);
        this.modelLoadError.emit(
          error instanceof Error
            ? error
            : new Error('Unknown error loading model'),
        );
      },
    );
  }

  private clearModel(): void {
    if (this.model) {
      this.scene.remove(this.model);
      this.model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.model = null;
    }
  }

  public addMarker(position: THREE.Vector3, note: string = ''): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(this.markerScale, 16, 16);
    const material = new THREE.MeshStandardMaterial({
      color: note ? 0x00ff00 : 0xff0000,
      emissive: note ? 0x00ff00 : 0xff0000,
      emissiveIntensity: 0.5,
    });

    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(position);
    marker.userData['isMarker'] = true;
    marker.userData['note'] = note;

    if (this.model) {
      this.model.add(marker);
    }

    return marker;
  }

  public removeMarker(marker: THREE.Mesh): void {
    if (this.model) {
      this.model.remove(marker);
    }
    marker.geometry.dispose();
    (marker.material as THREE.Material).dispose();
  }

  public updateMarkerNote(marker: THREE.Mesh, note: string): void {
    marker.userData['note'] = note;

    const material = marker.material as THREE.MeshStandardMaterial;
    const color = note ? 0x00ff00 : 0xff0000;
    material.color.setHex(color);
    material.emissive.setHex(color);
  }

  public handleCanvasClick(event: MouseEvent): void {
    if (!this.model || !this.renderer) return;
    if ((this.transformControls as any)?.dragging) return;
    if (event.button !== 0) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObjects(
      this.model.children,
      true,
    );

    if (intersects.length > 0) {
      const intersection = intersects[0];
      const clickedObject = intersection.object as THREE.Mesh;

      // Measurement modes take priority
      if (this.isMeasureDistanceMode) {
        this.handleDistanceMeasureClick(intersection.point);
        return;
      }
      if (this.isMeasureAngleMode) {
        this.handleAngleMeasureClick(intersection.point);
        return;
      }
      if (this.isMeasureAreaMode) {
        this.handleAreaMeasureClick(intersection.point);
        return;
      }

      // Check if clicked on a marker
      if (clickedObject.userData['isMarker']) {
        const markerIndex = this.markers.indexOf(clickedObject);
        if (markerIndex !== -1) {
          this.markerClick.emit(markerIndex);
          this.transformControls.detach();
          return;
        }
      }

      if (this.isAddMarkerMode && !this.markers.includes(clickedObject)) {
        const point = intersection.point;
        const localPoint = this.model.worldToLocal(point.clone());
        this.markerAdd.emit(localPoint);
        this.transformControls.detach();
        return;
      }

      if (this.isGizmoMode && this.model) {
        this.transformControls.attach(this.model);
        this.transformControls.setMode('translate');
      }
    } else {
      this.transformControls.detach();
    }
  }

  private handleDistanceMeasureClick(point: THREE.Vector3): void {
    this.addMeasurePoint(point);
    if (this.measurePoints.length === 2) {
      const rawDist = this.measurePoints[0].distanceTo(this.measurePoints[1]);
      const distMm = rawDist * this.measureScaleFactor;
      const label = this.formatLength(distMm);
      this.drawMeasureLine(this.measurePoints[0], this.measurePoints[1]);
      this.measurementResult.emit({
        type: 'distance',
        value: distMm / 10,
        unit: 'cm',
        label,
      });
      this.measurePoints = [];
    }
  }

  private handleAngleMeasureClick(point: THREE.Vector3): void {
    this.addMeasurePoint(point);
    if (this.measurePoints.length === 3) {
      const a = this.measurePoints[0];
      const b = this.measurePoints[1]; // vertex / köşe
      const c = this.measurePoints[2];
      const ba = new THREE.Vector3().subVectors(a, b).normalize();
      const bc = new THREE.Vector3().subVectors(c, b).normalize();
      const dot = THREE.MathUtils.clamp(ba.dot(bc), -1, 1);
      const angle = THREE.MathUtils.radToDeg(Math.acos(dot));
      const label = `${angle.toFixed(1)}°`;
      this.drawMeasureLine(a, b);
      this.drawMeasureLine(b, c);
      this.measurementResult.emit({ type: 'angle', value: angle, unit: '°', label });
      this.measurePoints = [];
    }
  }

  private handleAreaMeasureClick(point: THREE.Vector3): void {
    const n = this.measurePoints.length;
    if (n >= 1) {
      // Kenar ekle
      this.drawMeasureLine(this.measurePoints[n - 1], point);
      this.areaPerimeterRaw += this.measurePoints[n - 1].distanceTo(point);
    }
    this.addMeasurePoint(point);
    this.areaPointCount = this.measurePoints.length;
    this.updatePreviewLine(point, point); // preview başlangıcı
  }

  public finalizeMeasureArea(): void {
    const pts = this.measurePoints;
    if (pts.length >= 3) {
      // Son kenar: son nokta → ilk nokta
      const closingEdge = pts[pts.length - 1].distanceTo(pts[0]);
      this.drawMeasureLine(pts[pts.length - 1], pts[0]);
      const totalPerimRaw = this.areaPerimeterRaw + closingEdge;

      // Shoelace (Newell) — 3D polygon area
      const normal = this.computePolygonNormal(pts);
      let area = 0;
      for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const cross = new THREE.Vector3().crossVectors(pts[i], pts[j]);
        area += cross.dot(normal);
      }
      area = Math.abs(area) / 2;

      const areaMm2 = area * this.measureScaleFactor * this.measureScaleFactor;
      const perimMm = totalPerimRaw * this.measureScaleFactor;
      const areaLabel = this.formatArea(areaMm2);
      const perimLabel = this.formatLength(perimMm);
      const label = `${perimLabel} ${this.translate.instant('modelViewer.perimeterUnit')} · ${areaLabel} ${this.translate.instant('modelViewer.areaUnit')}`;
      this.measurementResult.emit({
        type: 'area',
        value: areaMm2 / 100,
        unit: 'cm^2',
        label,
        extra: {
          perimeter: perimMm / 10,
          perimeterLabel: perimLabel,
          areaLabel,
        }
      } as any);
    }
    this.removePreviewLine();
    this.areaPerimeterRaw = 0;
    this.areaPointCount = 0;
    this.measurePoints = [];
  }

  /** Newell's method — polygon normal */
  private computePolygonNormal(pts: THREE.Vector3[]): THREE.Vector3 {
    const n = new THREE.Vector3();
    for (let i = 0; i < pts.length; i++) {
      const cur = pts[i];
      const nxt = pts[(i + 1) % pts.length];
      n.x += (cur.y - nxt.y) * (cur.z + nxt.z);
      n.y += (cur.z - nxt.z) * (cur.x + nxt.x);
      n.z += (cur.x - nxt.x) * (cur.y + nxt.y);
    }
    return n.normalize();
  }

  /** Uzunluk gösterimi: her zaman cm (iç hesap mm). */
  private formatLength(mm: number): string {
    const cm = mm / 10;
    return `${cm.toFixed(2)} cm`;
  }

  /** Alan gösterimi: her zaman cm² (iç hesap mm²). */
  private formatArea(mm2: number): string {
    const cm2 = mm2 / 100;
    return `${cm2.toFixed(2)} cm²`;
  }

  private addMeasurePoint(worldPoint: THREE.Vector3): void {
    const dotGeo = new THREE.SphereGeometry(this.markerScale * 2, 12, 12);
    const dotMat = new THREE.MeshBasicMaterial({
      color: this.getMeasurementColorNumber(),
    });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.copy(worldPoint);
    dot.userData['isMeasureDot'] = true;
    dot.userData['baseScale'] = 1;
    this.scene.add(dot);
    this.measureDots.push(dot);
    this.measurePoints.push(worldPoint.clone());
  }

  private drawMeasureLine(from: THREE.Vector3, to: THREE.Vector3): void {
    const points = [from.clone(), to.clone()];
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    const mat = new THREE.LineBasicMaterial({
      color: this.getMeasurementColorNumber(),
      linewidth: 2,
    });
    const line = new THREE.Line(geo, mat);
    line.userData['isMeasureLine'] = true;
    this.scene.add(line);
    this.measureLines.push(line);
  }

  private addMeasureLabel(position: THREE.Vector3, text: string): void {
    const padding = 10;
    const fontSize = 22;
    const lineHeight = Math.round(fontSize * 1.3);
    const font = `bold ${fontSize}px Arial, sans-serif`;

    const lines = text.includes(' · ')
      ? text.split(' · ').map((s) => s.trim())
      : [text];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = font;

    let maxW = 0;
    for (const line of lines) {
      maxW = Math.max(maxW, ctx.measureText(line).width);
    }

    const w = Math.min(Math.ceil(maxW + padding * 2), 1024);
    const h = Math.ceil(padding * 2 + lines.length * lineHeight);

    canvas.width = w;
    canvas.height = h;

    ctx.font = font;
    ctx.fillStyle = 'rgba(30,30,30,0.85)';
    ctx.beginPath();
    ctx.roundRect(4, 4, w - 8, h - 8, 10);
    ctx.fill();
    ctx.fillStyle = this.getMeasurementColorHex();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < lines.length; i++) {
      const y = padding + lineHeight * (i + 0.5);
      ctx.fillText(lines[i], w / 2, y);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const spriteMat = new THREE.SpriteMaterial({ map: texture, depthTest: false });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.position.copy(position);
    const baseW = 256;
    const baseH = 64;
    const sw = this.markerScale * 20 * (w / baseW);
    const sh = this.markerScale * 5 * (h / baseH);
    sprite.scale.set(sw, sh, 1);
    sprite.userData['isMeasureLabel'] = true;
    sprite.userData['baseSW'] = sw;
    sprite.userData['baseSH'] = sh;
    this.scene.add(sprite);
    this.measureLabels.push(sprite);
  }

  public clearMeasurements(): void {
    this.measureDots.forEach((d) => {
      this.scene.remove(d);
      d.geometry.dispose();
      (d.material as THREE.Material).dispose();
    });
    this.measureLines.forEach((l) => {
      this.scene.remove(l);
      l.geometry.dispose();
      (l.material as THREE.Material).dispose();
    });
    this.measureLabels.forEach((s) => {
      this.scene.remove(s);
      (s.material as THREE.SpriteMaterial).map?.dispose();
      (s.material as THREE.Material).dispose();
    });
    this.measureDots = [];
    this.measureLines = [];
    this.measureLabels = [];
    this.measurePoints = [];
    this.removePreviewLine();
    this.areaPerimeterRaw = 0;
    this.areaPointCount = 0;
  }

  private updatePreviewLine(from: THREE.Vector3, to: THREE.Vector3): void {
    this.removePreviewLine();
    const geo = new THREE.BufferGeometry().setFromPoints([from.clone(), to.clone()]);
    const mat = new THREE.LineDashedMaterial({
      color: this.getMeasurementColorNumber(),
      dashSize: this.markerScale * 1.5,
      gapSize: this.markerScale * 1,
      opacity: 0.6,
      transparent: true,
    });
    const line = new THREE.Line(geo, mat);
    line.computeLineDistances();
    this.previewLine = line;
    this.scene.add(line);
  }

  private removePreviewLine(): void {
    if (this.previewLine) {
      this.scene.remove(this.previewLine);
      this.previewLine.geometry.dispose();
      (this.previewLine.material as THREE.Material).dispose();
      this.previewLine = null;
    }
  }

  /** Çift tıkla polygon kapat */
  public handleCanvasDblClick(event: MouseEvent): void {
    if (!this.isMeasureAreaMode) return;
    event.preventDefault();
    if (this.measurePoints.length >= 3) {
      this.finalizeMeasureArea();
    }
  }

  /** Alan modunda cursor'ı takip eden preview line */
  public handleCanvasMouseMove(event: MouseEvent): void {
    if (!this.isMeasureAreaMode || this.measurePoints.length === 0) return;
    if (!this.model || !this.renderer) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const py = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    const tmpPtr = new THREE.Vector2(px, py);

    this.raycaster.setFromCamera(tmpPtr, this.camera);
    const hits = this.raycaster.intersectObjects(this.model.children, true);
    if (hits.length > 0) {
      const last = this.measurePoints[this.measurePoints.length - 1];
      this.updatePreviewLine(last, hits[0].point);
    }
  }

  private handleGizmoModeChange(): void {
    if (!this.isGizmoMode) {
      this.transformControls.detach();
    }
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (!this.transformControls) return;

    switch (event.key.toLowerCase()) {
      case 't':
        if (!(this.transformControls as any).object) {
          // Gizmo mode should be toggled from parent
        } else {
          this.transformControls.setMode('translate');
        }
        break;
      case 'r':
        if ((this.transformControls as any).object) {
          this.transformControls.setMode('rotate');
        }
        break;
      case 's':
        if ((this.transformControls as any).object) {
          this.transformControls.setMode('scale');
        }
        break;
    }
  }

  public handleResize(): void {
    if (!this.renderer || !this.canvasRef) return;

    const container = this.canvasRef.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  private animate = (): void => {
    if (!this.renderer || !this.scene) {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.animationId = null;
      return;
    }

    this.animationId = requestAnimationFrame(this.animate);

    if (this.controls) {
      this.controls.update();

      const limit = 20;
      this.controls.target.x = THREE.MathUtils.clamp(
        this.controls.target.x,
        -limit,
        limit,
      );
      this.controls.target.z = THREE.MathUtils.clamp(
        this.controls.target.z,
        -limit,
        limit,
      );

      // Ölçüm noktalarını ve etiketlerini zoom'a göre ölçeklendir
      if (this.measureDots.length > 0 || this.measureLabels.length > 0) {
        const currentDist = this.camera.position.distanceTo(this.controls.target);
        const ratio = currentDist / this.refCameraDistance;
        for (const dot of this.measureDots) {
          dot.scale.setScalar(ratio);
        }
        for (const sprite of this.measureLabels) {
          sprite.scale.set(
            sprite.userData['baseSW'] * ratio,
            sprite.userData['baseSH'] * ratio,
            1,
          );
        }
      }
    }

    this.renderer.render(this.scene, this.camera);
  };

  public getModel(): THREE.Group | null {
    return this.model;
  }

  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  public getRaycaster(): THREE.Raycaster {
    return this.raycaster;
  }

  public getPointer(): THREE.Vector2 {
    return this.pointer;
  }

  public resetCamera(): void {
    if (!this.model || !this.controls || !this.camera) return;

    this.camera.position.copy(this.initialCameraPosition);
    this.controls.target.copy(this.initialCameraTarget);
    this.controls.update();
  }

  public setBrightness(value: number): void {
    this.brightness = value;
    this.updateLighting();
  }

  public setContrastMode(enabled: boolean): void {
    this.isHighContrast = enabled;
    this.updateLighting();
  }

  public setTextureEnabled(enabled: boolean): void {
    this.isTextureEnabled = enabled;
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (enabled) {
          const original = this.originalMaterials.get(child.uuid);
          if (original) {
            child.material = original;
          }
        } else {
          child.material = new THREE.MeshLambertMaterial({
            color: 0xdddddd,
            side: THREE.DoubleSide,
          });
        }
      }
    });
  }

  public toggleMarkers(visible: boolean): void {
    if (this.model) {
      this.model.traverse((child) => {
        if (child.userData['isMarker']) {
          child.visible = visible;
        }
      });
    }
  }

  private updateLighting(): void {
    if (!this.ambientLight || !this.dirLight || !this.hemiLight) return;

    if (this.isHighContrast) {
      this.ambientLight.intensity = 0.1 * this.brightness;
      this.hemiLight.intensity = 0.2 * this.brightness;
      this.dirLight.intensity = 2.0 * this.brightness;
    } else {
      this.ambientLight.intensity = 0.4 * this.brightness;
      this.hemiLight.intensity = 0.6 * this.brightness;
      this.dirLight.intensity = 1.2 * this.brightness;
    }
  }

  private handleWheel(event: WheelEvent): void {
    if (!this.controls.enabled) return;

    if (event.deltaY < 0 && this.model) {
      const distance = this.camera.position.distanceTo(this.controls.target);
      if (distance < this.controls.minDistance * 1.1) {
        return;
      }

      this.raycaster.setFromCamera(this.pointer, this.camera);
      const intersects = this.raycaster.intersectObjects(
        this.model.children,
        true,
      );

      if (intersects.length > 0) {
        const point = intersects[0].point;
        const alpha = 0.1;
        this.controls.target.lerp(point, alpha);
        this.controls.update();
      }
    }
  }

  private handleTouchStart(event: TouchEvent): void {
    if (!this.model || !this.renderer) return;
    if (event.touches.length !== 1) return;

    if (this.isAddMarkerMode || this.isGizmoMode || this.isMeasureDistanceMode || this.isMeasureAngleMode || this.isMeasureAreaMode) {
      event.preventDefault();
    }

    const touch = event.touches[0];
    this.getTouchPointerCoordinates(touch);
  }

  private handleTouchEnd(event: TouchEvent): void {
    if (!this.model || !this.renderer) return;
    if ((this.transformControls as any)?.dragging) return;
    if (event.changedTouches.length !== 1) return;

    if (this.isAddMarkerMode || this.isGizmoMode || this.isMeasureDistanceMode || this.isMeasureAngleMode || this.isMeasureAreaMode) {
      event.preventDefault();
    }

    const touch = event.changedTouches[0];
    this.getTouchPointerCoordinates(touch);

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObjects(
      this.model.children,
      true,
    );

    if (intersects.length > 0) {
      const intersection = intersects[0];
      const clickedObject = intersection.object as THREE.Mesh;

      if (this.isMeasureDistanceMode) {
        this.handleDistanceMeasureClick(intersection.point);
        return;
      }
      if (this.isMeasureAngleMode) {
        this.handleAngleMeasureClick(intersection.point);
        return;
      }
      if (this.isMeasureAreaMode) {
        this.handleAreaMeasureClick(intersection.point);
        return;
      }

      if (clickedObject.userData['isMarker']) {
        const markerIndex = this.markers.indexOf(clickedObject);
        if (markerIndex !== -1) {
          this.markerClick.emit(markerIndex);
          this.transformControls.detach();
          return;
        }
      }

      if (this.isAddMarkerMode && !this.markers.includes(clickedObject)) {
        const point = intersection.point;
        const localPoint = this.model.worldToLocal(point.clone());
        this.markerAdd.emit(localPoint);
        this.transformControls.detach();
        return;
      }

      if (this.isGizmoMode && this.model) {
        this.transformControls.attach(this.model);
        this.transformControls.setMode('translate');
      }
    } else {
      this.transformControls.detach();
    }
  }

  private getTouchPointerCoordinates(touch: Touch): void {
    if (!this.renderer) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private getMeasurementColorHex(): string {
    if (typeof window === 'undefined') {
      return this.fallbackMeasurementColorHex;
    }

    const cssColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-primary')
      .trim();

    return cssColor || this.fallbackMeasurementColorHex;
  }

  private getMeasurementColorNumber(): number {
    const colorHex = this.getMeasurementColorHex();
    const normalized = colorHex.replace('#', '').trim();
    const fullHex =
      normalized.length === 3
        ? normalized
            .split('')
            .map((char) => char + char)
            .join('')
        : normalized;

    if (!/^[A-Fa-f0-9]{6}$/.test(fullHex)) {
      return Number.parseInt(
        this.fallbackMeasurementColorHex.replace('#', ''),
        16,
      );
    }

    return Number.parseInt(fullHex, 16);
  }
}
